import { quotaApi } from '@/api/quotaApi'
import type { QuotaUsage } from '@/types/quota'
import { coalesceByKey } from '@/utils/coalesce'
import { useMemo } from 'react'
import { create } from 'zustand'
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware'

interface QuotaState {
	quotaUsage: Record<string, QuotaUsage> // keyed by tenant_id
	lastUpdated: Record<string, number> // timestamp per tenant
	loading: boolean
	error: string | null
}

interface QuotaActions {
	fetchQuotaUsage: (tenantId: string) => Promise<void>
	refreshQuotaUsage: (tenantId: string) => Promise<void>
	scheduleQuotaRefresh: (tenantId: string) => void
	setLoading: (loading: boolean) => void
	setError: (error: string | null) => void
	clearError: () => void
}

type QuotaStore = QuotaState & QuotaActions

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// In-flight request deduplication: prevents duplicate /metrics calls
const inFlightRequests = new Map<string, Promise<void>>()

/** Long enough to collapse a bulk action, short enough to land before anyone reads the CTA. */
const REFRESH_DEBOUNCE_MS = 400

const coalesce = coalesceByKey(REFRESH_DEBOUNCE_MS)

const initialState: QuotaState = {
	quotaUsage: {},
	lastUpdated: {},
	loading: false,
	error: null,
}

export const useQuotaStore = create<QuotaStore>()(
	devtools(
		persist(
			subscribeWithSelector((set, get) => ({
				...initialState,

				fetchQuotaUsage: async (tenantId: string) => {
					const state = get()
					const lastUpdate = state.lastUpdated[tenantId]

					if (lastUpdate && Date.now() - lastUpdate < CACHE_TTL && state.quotaUsage[tenantId]) {
						return
					}

					// Shares the in-flight map: every gated CTA calls this on mount, and an uncached tenant
					// would otherwise fan out one /metrics read per button.
					return get().refreshQuotaUsage(tenantId)
				},

				refreshQuotaUsage: async (tenantId: string) => {
					const existing = inFlightRequests.get(tenantId)
					if (existing) {
						return existing
					}

					const request = (async () => {
						set({ loading: true, error: null })
						try {
							const usage = await quotaApi.getQuotaUsage(tenantId)
							const now = Date.now()
							set(state => ({
								quotaUsage: { ...state.quotaUsage, [tenantId]: usage },
								lastUpdated: { ...state.lastUpdated, [tenantId]: now },
								loading: false,
							}))
						} catch (err) {
							set({ error: (err as Error).message, loading: false })
						} finally {
							inFlightRequests.delete(tenantId)
						}
					})()

					inFlightRequests.set(tenantId, request)
					return request
				},

				/** Fire and forget: the caller reported its own success, so a failed refresh stays quiet. */
				scheduleQuotaRefresh: (tenantId: string) => {
					if (!tenantId) {
						return
					}

					coalesce(tenantId, () => {
						// A read that started before the mutation still reports pre-mutation numbers, and the
						// in-flight dedupe would hand us exactly that one, then cache it for a full TTL.
						void Promise.resolve(inFlightRequests.get(tenantId)).then(() => get().refreshQuotaUsage(tenantId))
					})
				},

				setLoading: (loading: boolean) => set({ loading }),

				setError: (error: string | null) => set({ error }),

				clearError: () => set({ error: null }),
			})),
			{
				name: 'quota-storage',
				partialize: (state: QuotaState) => ({
					quotaUsage: state.quotaUsage,
					lastUpdated: state.lastUpdated,
				}),
			},
		),
		{
			name: 'quota-store',
		},
	),
)

// Selectors
export const useQuotaUsage = (tenantId: string) => useQuotaStore(state => state.quotaUsage[tenantId])

export const useQuotaActions = () => {
	const fetchQuotaUsage = useQuotaStore(state => state.fetchQuotaUsage)
	const refreshQuotaUsage = useQuotaStore(state => state.refreshQuotaUsage)
	const scheduleQuotaRefresh = useQuotaStore(state => state.scheduleQuotaRefresh)
	const setLoading = useQuotaStore(state => state.setLoading)
	const setError = useQuotaStore(state => state.setError)
	const clearError = useQuotaStore(state => state.clearError)

	return useMemo(
		() => ({
			fetchQuotaUsage,
			refreshQuotaUsage,
			scheduleQuotaRefresh,
			setLoading,
			setError,
			clearError,
		}),
		[fetchQuotaUsage, refreshQuotaUsage, scheduleQuotaRefresh, setLoading, setError, clearError],
	)
}
